import React, { useState, useEffect } from 'react';
import { Colors, IconButton, Button, Typography, CloseButton } from '@guildeducationinc/recess';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import ThumbDownIcon from '@guildeducationinc/recess/icons/ThumbDownIcon';
import ThumbUpIcon from '@guildeducationinc/recess/icons/ThumbUpIcon';
import { datadogRum } from '@datadog/browser-rum';
import { DATADOG_ENV } from '@src/utilities/envConfig';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import './ExplainerToolPopup.scss';

interface ExplainerToolPopupProps {
	selectedText: string;
	position: { x: number; y: number };
	onClose: () => void;
}

interface WebSocketMessage {
	part?: string;
	error?: string;
}

type FeedbackType = 'positive' | 'negative' | null;

const G4G_EMPLOYER_UUID = '7a02e216-7718-43d0-aa59-a67c4727620a';

export const ExplainerToolPopup: React.FC<ExplainerToolPopupProps> = ({
	selectedText,
	position,
	onClose,
}) => {
	const [response, setResponse] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [streamingResponse, setStreamingResponse] = useState<string>('');
	const [shouldConnect, setShouldConnect] = useState(false);
	const [feedback, setFeedback] = useState<FeedbackType>(null);
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { employer_uuid } = authUser;

	const apiKey = process.env.EXPLAINER_TOOL_API_KEY;
	const wsUrl = `${process.env.EXPLAINER_TOOL_WEBSOCKET_URL}?api_key=${apiKey}`;

	const { sendMessage, lastMessage, readyState } = useWebSocket(shouldConnect ? wsUrl : null, {
		onOpen: () => {
			if (selectedText.trim()) {
				const payload = {
					msg: selectedText,
				};
				sendMessage(JSON.stringify(payload));
			}
		},
		onClose: () => {
			setLoading(false);
			setShouldConnect(false);
		},
		onError: () => {
			setLoading(false);
			setResponse('Unable to connect to the simplification service. Please try again.');
			setShouldConnect(false);
		},
		shouldReconnect: () => false,
	});

	useEffect(() => {
		if (lastMessage !== null) {
			try {
				const data: WebSocketMessage = JSON.parse(lastMessage.data);

				if (data.error) {
					setLoading(false);
					setResponse(`Error: ${data.error}`);
					return;
				}

				if (data.part) {
					setStreamingResponse(prev => prev + data.part);
				}
			} catch (parseError) {
				setLoading(false);
				setResponse('Error: Unable to parse response');
			}
		}
	}, [lastMessage]);

	// Only render component for G4G
	if (employer_uuid !== G4G_EMPLOYER_UUID) {
		return null;
	}

	const handleSimplify = () => {
		if (!selectedText.trim()) return;

		const simplifyData = {
			selected_text: selectedText,
			environment: DATADOG_ENV,
			page_url: window.location.href,
			user_employer_uuid: employer_uuid,
			timestamp: new Date().toISOString(),
			feature: 'explainer_tool',
		};

		datadogRum.addAction('ExplainerToolSimplifyClick', simplifyData);

		setLoading(true);
		setResponse(null);
		setStreamingResponse('');
		setFeedback(null);
		setShouldConnect(true);
	};

	const handleFeedback = (type: 'positive' | 'negative') => {
		setFeedback(type);

		const feedbackData = {
			feedback_type: type,
			selected_text: selectedText,
			ai_response: response || streamingResponse,
			environment: DATADOG_ENV,
			page_url: window.location.href,
			user_employer_uuid: employer_uuid,
			timestamp: new Date().toISOString(),
			feature: 'explainer_tool',
		};

		datadogRum.addAction('ExplainerToolFeedback', feedbackData);
	};

	const isConnecting = readyState === ReadyState.CONNECTING;
	const finalResponse = response || streamingResponse;

	return (
		<div
			aria-label='Text simplification popup'
			className='explainer-tool-popup'
			role='dialog'
			style={{ left: position.x, top: position.y }}
		>
			<CloseButton
				aria-label='Close simplification popup'
				className='explainer-tool-popup__close-button'
				label='Close'
				onClick={onClose}
				testid='close-popup'
			/>

			{!finalResponse ? (
				<div className='explainer-tool-popup__content'>
					<Typography className='explainer-tool-popup__selected-text' variant='body1'>
						{loading || isConnecting ? (
							<div className='explainer-tool-popup__loading'>
								<div aria-hidden='true' className='explainer-tool-popup__loader'></div>
								We are simplifying your text...
							</div>
						) : (
							'Not sure what something means? Tap “Simplify” to clear it up!'
						)}
					</Typography>
					{!loading && !isConnecting && (
						<Button
							appearance='outline'
							aria-label='Simplify selected text'
							className='explainer-tool-popup__simplify-button'
							disabled={loading || isConnecting}
							onClick={handleSimplify}
							type='button'
						>
							Simplify
						</Button>
					)}
				</div>
			) : (
				<div className='explainer-tool-popup__response'>
					<Typography variant='body1'>{finalResponse}</Typography>
					<div className='explainer-tool-popup__footer'>
						<div className='explainer-tool-popup__footer-content'>
							<Typography className='explainer-tool-popup__footer-text' variant='label2'>
								Simplified with Anthropic
							</Typography>
							<div className='explainer-tool-popup__feedback-buttons'>
								<IconButton
									appearance='ghost'
									aria-label='This simplification was helpful'
									disabled={feedback === 'positive'}
									icon={
										<ThumbUpIcon
											color={feedback === 'positive' ? Colors.GreyShade : Colors.Grey}
											size={24}
										/>
									}
									label='This simplification was helpful'
									onClick={() => handleFeedback('positive')}
								/>
								<IconButton
									appearance='ghost'
									aria-label='This simplification was not helpful'
									disabled={feedback === 'negative'}
									icon={
										<ThumbDownIcon
											color={feedback === 'negative' ? Colors.GreyShade : Colors.Grey}
											size={24}
										/>
									}
									label='This simplification was not helpful'
									onClick={() => handleFeedback('negative')}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
